import { useCallback, useEffect, useRef, useState } from 'react';

import * as HeaderStore from '@Stores/Header';
import useMedias from '@Hooks/useMedias';
import { useVisibleModals } from '@Stores/ModalWindows';

import type { CSSProperties, MutableRefObject } from 'react';

export interface LocalRefs {
  refMainContent: MutableRefObject<HTMLDivElement>;
  refSidebar: MutableRefObject<HTMLDivElement>;
}

function useChangePositionSidebar({ refMainContent, refSidebar }: LocalRefs) {
  const { isMobileM } = useMedias();
  const refLastScroll = useRef(0);
  const howManyCanScroll = useRef(null);
  const howRestScroll = useRef(null);
  const visibleModals = useVisibleModals();
  const [positionSidebar, setPositionSidebar] = useState<CSSProperties>({});
  const { totalHeight } = HeaderStore.useSizes();
  const handleChangePositionSidebar = useCallback(() => {
    // Для расчета позиции скрола используем Math.abs(rectDocument.top) вместо window.pageYOffset
    // Потому что window.pageYOffset обнуляется во время блокировки скрола (например, во время открытия модальных окон)

    if (!refMainContent.current || visibleModals.length || !refSidebar.current) return;

    const rectDocument = document.documentElement.getBoundingClientRect();
    const rectContent = refMainContent.current.getBoundingClientRect();
    const rectSidebar = refSidebar.current.getBoundingClientRect();
    const diffScroll = refLastScroll.current - Math.abs(rectDocument.top);

    refLastScroll.current = Math.abs(rectDocument.top);

    if (typeof howRestScroll.current !== 'number') {
      howRestScroll.current = howManyCanScroll.current;
    }

    if (rectSidebar.top <= totalHeight) {
      howRestScroll.current += diffScroll;
    }

    // Количество оставшего скрола не должно превышать кол-во доступного скрола
    if (howRestScroll.current < 0) {
      howRestScroll.current = 0;
    }
    if (howRestScroll.current > howManyCanScroll.current) {
      howRestScroll.current = howManyCanScroll.current;
    }

    // Фиксируем нижнюю границу сайдбара и искусственно смещаем по оси Y на кол-во оставшегося скрола
    if (Math.floor(rectContent.top) <= totalHeight && rectDocument.top !== 0 && !isMobileM) {
      setPositionSidebar({
        position: 'sticky',
        top: `${howRestScroll.current - howManyCanScroll.current + totalHeight}px`,
      });
    }
  }, [refMainContent, refSidebar, totalHeight, isMobileM, visibleModals]);

  useEffect(() => {
    if (isMobileM) setPositionSidebar({});
  }, [isMobileM]);

  useEffect(() => {
    setTimeout(handleChangePositionSidebar, 200);
    window.addEventListener('scroll', handleChangePositionSidebar, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleChangePositionSidebar);
    };
  }, [handleChangePositionSidebar]);

  const handleCalc = useCallback(() => {
    if (!refSidebar.current) return;

    const rectSidebar = refSidebar.current.getBoundingClientRect();

    howManyCanScroll.current = rectSidebar.height - window.innerHeight + 30 + totalHeight;
  }, [refSidebar, totalHeight]);

  useEffect(() => {
    const cleanup = () => {
      window.removeEventListener('resize', handleCalc);
      window.removeEventListener('resize', handleChangePositionSidebar);
    };

    if (isMobileM) return cleanup;

    handleCalc();
    window.addEventListener('resize', handleCalc);
    window.addEventListener('resize', handleChangePositionSidebar);

    return cleanup;
  }, [handleCalc, handleChangePositionSidebar, isMobileM]);

  return positionSidebar;
}

export default useChangePositionSidebar;
